// ABOUTME: Welcome step - introduces the app with branding and tagline
// ABOUTME: First step of onboarding flow with hero video area

import { useRef, useEffect } from "react";
import { motion } from "framer-motion";

interface WelcomeStepProps {
  onContinue: () => void;
  onSkip: () => void;
}

// Wordmark component - Touchstone branding
function Wordmark() {
  return (
    <div className="welcome-wordmark">
      <span className="welcome-wordmark__text">Internet</span>
      <span className="welcome-wordmark__icon">
        <img src="/icon-192.png" alt="" width={28} height={28} />
      </span>
      <span className="welcome-wordmark__text">Sculptures</span>
    </div>
  );
}

export function WelcomeStep({ onContinue, onSkip }: WelcomeStepProps) {
  const videoRef = useRef<HTMLVideoElement>(null);

  // Fallback: manually trigger play if autoplay fails (common on mobile)
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const attemptPlay = () => {
      video.play().catch(() => {
        // Autoplay blocked - that's okay, poster will show
      });
    };

    // Try to play when video is ready
    if (video.readyState >= 3) {
      attemptPlay();
    } else {
      video.addEventListener("canplay", attemptPlay, { once: true });
    }
  }, []);

  return (
    <div className="onboarding-step welcome-step">
      {/* Hero area - video */}
      <motion.div
        className="welcome-step__hero"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.6 }}
      >
        <div className="welcome-step__hero-content">
          <video
            ref={videoRef}
            className="welcome-step__hero-video"
            autoPlay
            loop
            muted
            playsInline
            src="/objects/montage-h264.mp4"
            poster="/objects/fortune-cookie.png"
            preload="auto"
          />
        </div>
      </motion.div>

      {/* Content */}
      <motion.div
        className="onboarding-step__content"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, delay: 0.3 }}
      >
        {/* Wordmark */}
        <Wordmark />

        {/* Tagline */}
        <motion.p
          className="onboarding-step__tagline"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.4, delay: 0.4 }}
        >
          Software you can touch
        </motion.p>

        {/* Description */}
        <motion.p
          className="onboarding-step__subtitle"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.4, delay: 0.5 }}
        >
          Touchstone helps you personalize and manage your internet sculptures.
        </motion.p>
      </motion.div>

      {/* Fixed bottom actions */}
      <motion.div
        className="onboarding-step__footer"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, delay: 0.6 }}
      >
        <button className="onboarding-step__button" onClick={onContinue}>
          Get Started
        </button>
        <button className="onboarding-step__skip" onClick={onSkip}>
          skip for now
        </button>
      </motion.div>
    </div>
  );
}
